import { useEffect, useMemo, useRef, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import { getAuth, getIdTokenResult } from "firebase/auth";

import { db, functions, storage } from "../firebaseConfig";
import Header from "../components/HeaderV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/PointV2.module.css";
import ErrMsg from "../ErrMsg";
import { BlockInput, PhotoInput, ToggleSwitch, photoResize } from "../components/Inputs";
import { PointType, usePoint } from "../PointContext";
import { useMember } from "../MemberContext";
import { Image as ImageIcon, Trash2, X } from "lucide-react";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

export interface WithdrawType {
    id: string;
    title: string;
    amount: string;
    type: string;
    image: string;
    active: boolean;
    timestamp?: number;
}

const PointV2 = () => {
    const { withdraws } = usePoint();
    const { members } = useMember();
    const { withdrawMethods } = useWithdrawMethods();
    const [addPoint, setAddPoint] = useState<boolean>(false);
    const [addWithdraw, setAddWithdraw] = useState<boolean>(false);
    const [editWithdraw, setEditWithdraw] = useState<WithdrawType | null>(null);
    const [updatingMethodMap, setUpdatingMethodMap] = useState<Record<string, boolean>>({});

    const totalWithdrawAmount = withdraws.reduce((sum, point) => sum + Math.abs(Number(point.pointDelta || 0)), 0);

    const toggleWithdrawMethod = async (method: WithdrawType) => {
        const partnerId = sessionStorage.getItem("partnerId");
        if (!partnerId) return alert("파트너 정보를 찾을 수 없습니다.");

        setUpdatingMethodMap((prev) => ({ ...prev, [method.id]: true }));
        try {
            await updateDoc(doc(db, `partner/${partnerId}/withdraw`, method.id), {
                active: !method.active,
            });
        } catch (err) {
            console.error(err);
            alert("환전 방법 노출 상태 변경 중 오류가 발생했습니다.");
        } finally {
            setUpdatingMethodMap((prev) => ({ ...prev, [method.id]: false }));
        }
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>포인트 관리</h4>
                    <p className={common["text--copy"]}>포인트 지급, 환전 방법, 환전 승인 요청을 관리합니다.</p>

                    <div className={style["summary-grid"]}>
                        <SummaryCard label="환전 대기" value={`${withdraws.length}건`} />
                        <SummaryCard label="대기 요청 금액" value={`₩${totalWithdrawAmount.toLocaleString()}`} />
                        <SummaryCard label="전체 회원" value={`${members.length}명`} />
                        <SummaryCard label="환전 방법" value={`${withdrawMethods.length}개`} />
                    </div>

                    <section className={style["action-panel"]}>
                        <div>
                            <p className={style["section-kicker"]}>수동 지급</p>
                            <h5>포인트 지급</h5>
                            <p>회원 검색 후 지정한 포인트를 즉시 지급합니다.</p>
                        </div>
                        <button className={style["button--primary"]} onClick={() => setAddPoint(true)}>
                            포인트 지급
                        </button>
                    </section>

                    <WithdrawMethodPanel
                        methods={withdrawMethods}
                        onAdd={() => setAddWithdraw(true)}
                        onEdit={(withdraw) => setEditWithdraw(withdraw)}
                        onToggle={toggleWithdrawMethod}
                        updatingMap={updatingMethodMap}
                    />
                    <PointTable />
                </div>
            </div>

            {addPoint && <AddPoint close={() => setAddPoint(false)} />}
            {addWithdraw && <WithdrawModal mode="CREATE" close={() => setAddWithdraw(false)} />}
            {editWithdraw && (
                <WithdrawModal mode="EDIT" withdraw={editWithdraw} close={() => setEditWithdraw(null)} />
            )}
        </>
    );
};

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
    <div className={style["summary-card"]}>
        <p>{label}</p>
        <strong>{value}</strong>
    </div>
);

const PointTable = () => {
    const { withdraws } = usePoint();
    const [search, setSearch] = useState<string>("");
    const [selected, setSelected] = useState<PointType | null>(null);
    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});

    const filteredWithdraws = withdraws.filter((point) => {
        const keyword = search.toLowerCase();
        const name = point.member?.name ?? "";
        const phone = point.member?.phone ?? "";
        return (
            search === "" ||
            name.toLowerCase().includes(keyword) ||
            phone.toLowerCase().includes(keyword) ||
            formatPhone(phone).toLowerCase().includes(keyword)
        );
    });

    const filteredAmount = filteredWithdraws.reduce((sum, point) => sum + Math.abs(Number(point.pointDelta || 0)), 0);

    const confirmWithdraw = async (point: PointType) => {
        if (!window.confirm("환전 지급 완료 처리하시겠습니까?")) return;

        setLoadingConfirmMap((prev) => ({ ...prev, [point.id]: true }));
        try {
            await httpsCallable(functions, "completeWithdraw")({ docId: point.id });
        } catch (err: any) {
            console.error(err);
            alert(ErrMsg[err.message] || "에러가 발생했습니다");
        } finally {
            setLoadingConfirmMap((prev) => ({ ...prev, [point.id]: false }));
        }
    };

    return (
        <section className={style["point-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>환전 승인</p>
                    <h5 className={style["panel-title"]}>대기 중인 환전 요청</h5>
                    <p className={style["panel-copy"]}>
                        {filteredWithdraws.length}건 · ₩{filteredAmount.toLocaleString()}
                    </p>
                </div>
            </div>
            <div className={style["toolbar"]}>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="이름 또는 전화번호로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className={style["wrapper--point-list"]}>
                <div className={[style["point-row"], style["point-row--head"]].join(" ")}>
                    <p>이름</p>
                    <p>전화번호</p>
                    <p>요청일시</p>
                    <p>요청금액</p>
                    <p>관리</p>
                </div>
                {filteredWithdraws.length === 0 && <div className={style["empty-state"]}>대기 중인 환전 요청이 없습니다.</div>}
                {filteredWithdraws.map((point) => (
                    <PointRow
                        key={point.id}
                        point={point}
                        loading={loadingConfirmMap[point.id] ?? false}
                        onPay={() => confirmWithdraw(point)}
                        onOpen={() => setSelected(point)}
                    />
                ))}
            </div>
            {selected && <WithdrawDetail point={selected} close={() => setSelected(null)} />}
        </section>
    );
};

const PointRow = ({
    point,
    loading,
    onPay,
    onOpen,
}: {
    point: PointType;
    loading: boolean;
    onPay: () => void;
    onOpen: () => void;
}) => (
    <div className={style["unit--point-list"]} onClick={onOpen}>
        <div className={style["point-row"]}>
            <p className={style["row-strong"]}>{point.member?.name ?? "이름 없음"}</p>
            <p>{formatPhone(point.member?.phone) || "-"}</p>
            <p>{formatDateTime(point.timestamp)}</p>
            <p className={style["amount"]}>₩{Math.abs(Number(point.pointDelta || 0)).toLocaleString()}</p>
            <div className={style["row-actions"]}>
                <button
                    className={style["button--pay"]}
                    disabled={loading}
                    onClick={(e) => {
                        e.stopPropagation();
                        onPay();
                    }}
                >
                    {loading ? "처리 중" : "완료처리"}
                </button>
                <button
                    className={style["button--ghost"]}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpen();
                    }}
                >
                    상세
                </button>
            </div>
        </div>
    </div>
);

const WithdrawDetail = ({ point, close }: { point: PointType; close: () => void }) => {
    const [active, setActive] = useState<boolean>(false);
    useEffect(() => setActive(true), []);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>환전 요청 상세</p>
                        <h4 className={style["modal-title"]}>{point.member?.name ?? "이름 없음"}</h4>
                        <p className={style["modal-copy"]}>{formatDateTime(point.timestamp)}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["detail-grid"]}>
                            <InfoItem label="전화번호" value={formatPhone(point.member?.phone) || "-"} />
                            <InfoItem label="요청금액" value={`₩${Math.abs(Number(point.pointDelta || 0)).toLocaleString()}`} />
                            <InfoItem label="회원 UID" value={point.owner || "-"} />
                            <InfoItem label="요청 ID" value={point.id} />
                        </div>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

const WithdrawMethodPanel = ({
    methods,
    onAdd,
    onEdit,
    onToggle,
    updatingMap,
}: {
    methods: WithdrawType[];
    onAdd: () => void;
    onEdit: (withdraw: WithdrawType) => void;
    onToggle: (withdraw: WithdrawType) => void;
    updatingMap: Record<string, boolean>;
}) => (
    <section className={style["point-panel"]}>
        <div className={style["panel-header"]}>
            <div>
                <p className={style["section-kicker"]}>환전 방법</p>
                <h5 className={style["panel-title"]}>등록된 환전 방법</h5>
                <p className={style["panel-copy"]}>회원이 선택할 수 있는 환전 항목을 관리합니다.</p>
            </div>
            <button className={style["button--primary"]} onClick={onAdd}>
                환전 방법 추가
            </button>
        </div>
        <div className={style["wrapper--point-list"]}>
            <div className={[style["method-row"], style["point-row--head"]].join(" ")}>
                <p>이미지</p>
                <p>제목</p>
                <p>금액</p>
                <p>상태</p>
                <p>관리</p>
            </div>
            {methods.length === 0 && <div className={style["empty-state"]}>등록된 환전 방법이 없습니다.</div>}
            {methods.map((method) => (
                <div key={method.id} className={style["unit--point-list"]}>
                    <div className={style["method-row"]}>
                        <div className={style["method-thumb"]}>
                            {method.image ? <img src={method.image} alt={method.title} /> : <span>이미지 없음</span>}
                        </div>
                        <p className={style["row-strong"]}>{method.title}</p>
                        <p className={style["amount"]}>₩{Number(method.amount || 0).toLocaleString()}</p>
                        <div className={style["method-switch"]}>
                            <ToggleSwitch
                                checked={method.active}
                                disabled={updatingMap[method.id] ?? false}
                                onChange={() => onToggle(method)}
                            />
                        </div>
                        <div className={style["row-actions"]}>
                            <button className={style["button--ghost"]} onClick={() => onEdit(method)}>
                                관리
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

const AddPoint = ({ close }: { close: () => void }) => {
    const partnerId = sessionStorage.getItem("partnerId");
    const { members } = useMember();
    const [search, setSearch] = useState<string>("");
    const [selectedMember, setSelectedMember] = useState<MemberType | null>(null);
    const [point, setPoint] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => setActive(true), []);

    const filteredMembers = useMemo(() => {
        const keyword = search.toLowerCase();
        return members.filter((member) => {
            return (
                search === "" ||
                member.name.toLowerCase().includes(keyword) ||
                member.phone.toLowerCase().includes(keyword) ||
                formatPhone(member.phone).toLowerCase().includes(keyword)
            );
        });
    }, [members, search]);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const givePoint = async () => {
        if (!selectedMember) return alert("지급할 회원을 선택해주세요.");
        if (!point || Number(point) <= 0) return alert("지급할 포인트를 입력해주세요.");

        setLoading(true);
        try {
            await httpsCallable(functions, "addPoint")({
                uid: selectedMember.uid,
                pointDelta: Number(point),
                partnerId,
            });
            hide();
        } catch (err: any) {
            console.error(err);
            alert(ErrMsg[err.message] || "에러가 발생했습니다");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>수동 지급</p>
                        <h4 className={style["modal-title"]}>포인트 지급</h4>
                        <p className={style["modal-copy"]}>회원 검색 후 지급할 포인트를 입력합니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>회원 선택</h5>
                            <p>이름 또는 전화번호로 검색할 수 있습니다.</p>
                        </div>
                        <input
                            className={style["input--member-filter"]}
                            type="text"
                            placeholder="이름 또는 전화번호로 검색"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className={style["container--members"]}>
                            {filteredMembers.map((member) => (
                                <div
                                    key={member.uid}
                                    className={[
                                        style["unit--member-list"],
                                        selectedMember?.uid === member.uid && style["unit--member-list--selected"],
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    onClick={() => setSelectedMember(member)}
                                >
                                    <div className={style["member-main"]}>
                                        <p>{member.name}</p>
                                        <span>{formatPhone(member.phone) || "전화번호 없음"}</span>
                                    </div>
                                    <span
                                        className={[
                                            style["member-select-badge"],
                                            selectedMember?.uid === member.uid && style["member-select-badge--selected"],
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        {selectedMember?.uid === member.uid ? "선택됨" : "선택"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>지급 포인트</h5>
                            <p>선택한 회원에게 즉시 지급됩니다.</p>
                        </div>
                        <input
                            className={style["input--member-point"]}
                            type="number"
                            placeholder="지급할 포인트 입력"
                            value={point}
                            onChange={(e) => setPoint(e.target.value)}
                        />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={givePoint} disabled={loading}>
                        {loading ? "지급 중" : "지급"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const WithdrawModal = ({
    mode,
    withdraw,
    close,
}: {
    mode: "CREATE" | "EDIT";
    withdraw?: WithdrawType;
    close: () => void;
}) => {
    const init = useRef(true);
    const [title, setTitle] = useState<string>(withdraw?.title || "");
    const [titleError, setTitleError] = useState<string>("");
    const [amount, setAmount] = useState<string>(withdraw?.amount ? String(withdraw.amount) : "");
    const [amountError, setAmountError] = useState<string>("");
    const [activeFlag, setActiveFlag] = useState<boolean>(withdraw?.active ?? true);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoError, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
    const [overlayActive, setOverlayActive] = useState<boolean>(false);

    useEffect(() => setOverlayActive(true), []);
    useEffect(() => {
        if (init.current) return;
        setTitleError(!title.trim() ? "제목을 입력해주세요." : "");
    }, [title]);
    useEffect(() => {
        if (init.current) return;
        setAmountError(!amount || Number(amount) <= 0 ? "올바른 금액을 입력해주세요." : "");
    }, [amount]);
    useEffect(() => {
        init.current = false;
    }, []);

    const hide = () => {
        setOverlayActive(false);
        setTimeout(close, 240);
    };

    const saveWithdraw = async () => {
        if (!title.trim()) return setTitleError("제목을 입력해주세요.");
        if (!amount || Number(amount) <= 0) return setAmountError("올바른 금액을 입력해주세요.");
        if (mode === "CREATE" && !photo) return setPhotoError("이미지를 업로드해주세요.");

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return alert("로그인 상태가 아닙니다.");

        setLoading(true);
        try {
            await user.getIdToken(true);
            const token = await getIdTokenResult(user, true);
            const claimPartnerId = (token.claims as any)?.partnerId as string | undefined;
            const claimType = (token.claims as any)?.type as string | undefined;

            if (claimType !== "GROUP_ADMIN" || !claimPartnerId) {
                alert("권한이 없거나 partnerId 정보를 확인할 수 없습니다.");
                return;
            }

            const docRef =
                mode === "EDIT" && withdraw
                    ? doc(db, `partner/${claimPartnerId}/withdraw`, withdraw.id)
                    : doc(collection(db, `partner/${claimPartnerId}/withdraw`));

            let imageUrl = withdraw?.image || "";
            if (photo) {
                const resized = await photoResize(photo, "0");
                const storageRef = ref(storage, `partner/${claimPartnerId}/withdraw/${docRef.id}.png`);
                const uploadTask = uploadBytesResumable(storageRef, resized);

                await new Promise<void>((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => setProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                        reject,
                        () => resolve()
                    );
                });

                imageUrl = await getDownloadURL(ref(storage, `partner/${claimPartnerId}/withdraw/${docRef.id}.png`));
            }

            if (mode === "CREATE") {
                await setDoc(docRef, {
                    id: docRef.id,
                    title: title.trim(),
                    amount: Number(amount),
                    type: "COUPON",
                    active: activeFlag,
                    image: imageUrl,
                    timestamp: Date.now(),
                });
            } else {
                await updateDoc(docRef, {
                    title: title.trim(),
                    amount: Number(amount),
                    active: activeFlag,
                    image: imageUrl,
                });
            }

            hide();
        } catch (err) {
            console.error(err);
            alert("에러가 발생했습니다");
        } finally {
            setLoading(false);
        }
    };

    const deleteWithdraw = async () => {
        if (!withdraw || !window.confirm("환전 방법을 삭제하시겠습니까?")) return;

        setLoadingDelete(true);
        try {
            const partnerId = sessionStorage.getItem("partnerId");
            if (!partnerId) return alert("파트너 정보를 찾을 수 없습니다.");

            if (withdraw.image) {
                await deleteObject(ref(storage, `partner/${partnerId}/withdraw/${withdraw.id}.png`)).catch(() => {});
            }
            await deleteDoc(doc(db, `partner/${partnerId}/withdraw`, withdraw.id));
            hide();
        } catch (err) {
            console.error(err);
            alert("삭제 중 오류가 발생했습니다.");
        } finally {
            setLoadingDelete(false);
        }
    };

    return (
        <div className={[common["container--overlay"], overlayActive && common["container--overlay--active"]].join(" ")}>
            <div
                className={[
                    common["unit--overlay"],
                    style["modal"],
                    style["modal--withdraw"],
                    overlayActive && common["unit--overlay--active"],
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>환전 방법</p>
                        <h4 className={style["modal-title"]}>{mode === "CREATE" ? "환전 방법 추가" : "환전 방법 수정"}</h4>
                        <p className={style["modal-copy"]}>회원에게 표시되는 환전 항목입니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        <X size={18} />
                    </button>
                </div>

                <div className={[common["wrapper--overlay"], style["modal-body"]].join(" ")}>
                    <div className={style["modal-grid"]}>
                        <section className={style["form-section"]}>
                            <div className={style["section-head"]}>
                                <h5>기본 정보</h5>
                                <p>제목, 필요 포인트, 사용 여부를 설정합니다.</p>
                            </div>
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput type="text" placeholder="제목" value={title} setValue={setTitle} error={titleError} />
                                <BlockInput type="number" placeholder="필요 포인트" value={amount} setValue={setAmount} error={amountError} />
                                <div className={style["method-status-row"]}>
                                    <span className={style["method-status-label"]}>사용여부</span>
                                    <div className={style["method-switch"]}>
                                        <ToggleSwitch checked={activeFlag} onChange={() => setActiveFlag((prev) => !prev)} />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {mode === "EDIT" && (
                            <section className={[style["form-section"], style["form-section--danger"]].join(" ")}>
                                <div className={style["section-head"]}>
                                    <h5>삭제</h5>
                                    <p>삭제한 환전 방법은 회원 화면에서 더 이상 표시되지 않습니다.</p>
                                </div>
                                <button className={style["button--secondary"]} onClick={deleteWithdraw} disabled={loadingDelete}>
                                    <Trash2 size={16} />
                                    {loadingDelete ? "삭제 중" : "환전 방법 삭제"}
                                </button>
                            </section>
                        )}
                    </div>

                    <aside className={style["photo-panel"]}>
                        <div className={style["section-head"]}>
                            <div className={style["photo-title"]}>
                                <ImageIcon size={18} />
                                <h5>환전 방법 이미지</h5>
                            </div>
                            <p>환전 항목에 표시될 이미지를 등록합니다.</p>
                        </div>
                        <div className={style["photo-input-wrap"]}>
                            <PhotoInput
                                photo={photo}
                                setPhoto={(file) => setPhoto(file)}
                                progress={progress}
                                error={photoError}
                                existingImageUrl={withdraw?.image}
                            />
                        </div>
                    </aside>
                </div>

                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={saveWithdraw} disabled={loading}>
                        {loading ? "저장 중" : "저장"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const useWithdrawMethods = () => {
    const partnerId = sessionStorage.getItem("partnerId");
    const [withdrawMethods, setWithdrawMethods] = useState<WithdrawType[]>([]);

    useEffect(() => {
        if (!partnerId) return;

        const unsubscribe = onSnapshot(query(collection(db, `partner/${partnerId}/withdraw`)), (snapshot) => {
            const list = snapshot.docs
                .map((doc) => ({ ...(doc.data() as WithdrawType), id: doc.id }))
                .sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
            setWithdrawMethods(list);
        });
        return () => unsubscribe();
    }, [partnerId]);

    return { withdrawMethods };
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-item"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + (phone || "")).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : phone || "";
};

const formatDateTime = (timestamp?: number) => (timestamp ? new Date(timestamp).toLocaleString("ko-KR") : "-");

export default PointV2;
